/**
 * Privacy Policy (#126, FR-13.1). Satisfies the privacy-**notice** half of #41 (the
 * export/delete cascade remains #41). Static, unauthenticated, prerenderable copy;
 * product name from {@link BRAND_NAME}, entity/contact via {@link LegalPage}.
 */

import { LegalPage } from '../ui/legal/LegalPage';
import { BRAND_NAME } from '../ui/brand';

export function PrivacyPage(): React.JSX.Element {
  return (
    <LegalPage title="Privacy Policy" testId="legal-privacy" path="/privacy">
      <section>
        <h2>1. Scope</h2>
        <p>
          This policy explains what information {BRAND_NAME} collects, why, and your
          choices. It applies to your use of the Service.
        </p>
      </section>
      <section>
        <h2>2. Information we collect</h2>
        <ul>
          <li>
            <strong>Account data</strong> — for registered accounts, your sign-in email
            and a display name.
          </li>
          <li>
            <strong>Optional profile data</strong> — a birth year (used only to derive a
            coarse age band for age-appropriate content; the raw year is never shared) and
            an optional profile picture. Uploaded pictures are automatically screened for
            prohibited content before they are saved (see{' '}
            <em>Automated processing and AI</em> below).
          </li>
          <li>
            <strong>Voice data (AI-host games only)</strong> — if you play a game run by
            the AI host, your spoken answers are captured as audio and converted to text
            so the host can judge them. The audio is processed in real time and is not
            retained after your answer.
          </li>
          <li>
            <strong>Gameplay data</strong> — games played, scores, and standings used for
            leaderboards.
          </li>
          <li>
            <strong>Payment data</strong> — when you buy credits, our payment processor
            handles your card details; we receive only a confirmation and a record of the
            purchase, not your full card number.
          </li>
          <li>
            <strong>Technical data</strong> — basic device/connection information and
            local storage used to keep you signed in and to operate the game.
          </li>
        </ul>
      </section>
      <section>
        <h2>3. How we use it</h2>
        <p>
          To provide and secure the Service, run games and leaderboards, generate game
          content, screen uploaded images for prohibited content, process purchases,
          prevent abuse, and comply with law. We do not sell your personal information.
        </p>
      </section>
      <section>
        <h2>4. Service providers</h2>
        <p>
          We rely on third-party processors — for example, cloud hosting, authentication,
          payments, AI content generation and moderation, and (for the AI host)
          speech-to-text and text-to-speech — to run the Service. They process data on our
          behalf under appropriate safeguards.
        </p>
      </section>
      <section>
        <h2>5. Data retention and your choices</h2>
        <p>
          We keep information for as long as needed to provide the Service and meet legal
          obligations. You may edit or clear optional profile fields at any time.
          Signed-in users can <strong>export</strong> a copy of their personal data or{' '}
          <strong>delete</strong> their account and data at any time from the profile page
          (deletion removes your profile, game history, leaderboard standing, picture, and
          uploaded packs; a purchase/credit ledger is anonymized but retained as a
          financial record).
        </p>
      </section>
      <section>
        <h2>6. Automated processing and AI</h2>
        <p>We use artificial-intelligence services to operate parts of the Service:</p>
        <ul>
          <li>
            <strong>Game content</strong> — boards and clues are generated by an AI model.
            To tailor them we provide your category preferences, the coarse age band
            derived from your birth year, and which clues your group has already seen. We
            do not send your name or account identifiers.
          </li>
          <li>
            <strong>Image screening</strong> — profile pictures you upload are
            automatically checked by an image-moderation service before they are saved.
            Pictures that fail screening are rejected and not stored. If you believe a
            picture was rejected in error, contact us using the details below.
          </li>
          <li>
            <strong>AI host (optional)</strong> — in games run by the AI host, clue
            prompts are converted to speech, and your spoken answers are captured as audio
            and transcribed to text so the host can judge them. Voice audio is processed
            in real time and is not retained.
          </li>
        </ul>
        <p>
          These features rely on third-party AI providers acting as our service providers
          (see above).
        </p>
      </section>
      <section>
        <h2>7. Changes</h2>
        <p>We may update this policy; the version identifier above changes when we do.</p>
      </section>
    </LegalPage>
  );
}
